import { reportClientError } from '../lib/errorReporter.ts';
import { outageNote } from '../lib/outage.ts';
import type { ChatSummary, MessageDto, ReactionView, TranscriptView } from './client.ts';
import { wsUrl } from './origin.ts';

/** Mirrors ServerEvent in server/src/realtime.ts. */
export type ServerEvent =
  | { type: 'message:new'; message: MessageDto }
  | {
      type: 'message:read';
      conversationId: string;
      messageIds: string[];
      readAt: number;
      /** Who read them; only one's own receipt clears the unread badge. */
      readerId: string;
    }
  | {
      type: 'message:edited';
      conversationId: string;
      messageId: string;
      body: string;
      editedAt: number;
    }
  // Gone from this recipient's thread. `forEveryone` separates «у всех» from
  // «у меня» for the one thing that turns on it — a quote of this message in
  // somebody's reply, which only the first of the two blanks out.
  | {
      type: 'message:deleted';
      conversationId: string;
      messageId: string;
      forEveryone: boolean;
    }
  | {
      type: 'message:reaction';
      conversationId: string;
      messageId: string;
      reactions: ReactionView[];
    }
  // The recipient played a кружочек, which is what greys out its ring.
  | { type: 'message:viewed'; conversationId: string; messageId: string; viewedAt: number }
  // A voice message started being recognised, or finished. One payload for the
  // whole conversation: what was said is not a different fact per reader, and it
  // lands on the sender's device too, whose bubble grows the same block of text.
  | {
      type: 'message:transcript';
      conversationId: string;
      messageId: string;
      transcript: TranscriptView;
    }
  | { type: 'presence'; userId: string; online: boolean; lastSeenAt: number | null }
  | { type: 'typing'; conversationId: string; userId: string; typing: boolean }
  | { type: 'conversation:new'; chat: ChatSummary }
  // A group this viewer is still in changed shape — somebody left, or was
  // removed — so «N участников» and the roster are stale.
  | { type: 'conversation:updated'; chat: ChatSummary }
  // This viewer is out of that conversation: they left, or were removed.
  | { type: 'conversation:removed'; conversationId: string }
  // ---- calls ----
  //
  // `call:incoming` also arrives on a socket that connects *after* the invite
  // was placed, which is what makes being called with the app closed work: the
  // push wakes the app, the socket opens, and the server hands over the call
  // that has been ringing all along. `startedAt` says how long that has been.
  | {
      type: 'call:incoming';
      callId: string;
      conversationId: string;
      fromUserId: string;
      fromName: string;
      fromAvatarId: string | null;
      video: boolean;
      startedAt: number;
    }
  // The server has named the call this device just placed. Only this socket
  // gets it, and only an outgoing call has anything to do with it: until it
  // arrives the caller holds a call with an empty id, which is why cancelling
  // during the ring used to send nothing at all.
  | { type: 'call:placed'; callId: string; conversationId: string }
  // `video: false` means they answered with «Ответить без видео».
  | { type: 'call:accepted'; callId: string; video: boolean }
  // Another of this viewer's devices picked up; this one stops ringing.
  | { type: 'call:cancelled'; callId: string }
  // SDP and ICE, opaque to the server that forwarded them.
  | { type: 'call:signal'; callId: string; fromUserId: string; payload: unknown }
  | {
      type: 'call:ended';
      callId: string;
      reason: 'hangup' | 'declined' | 'busy' | 'timeout' | 'failed' | 'cancelled';
      durationMs: number;
    };

type Listener = (event: ServerEvent) => void;
type StatusListener = (connected: boolean) => void;

const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 15_000;

/**
 * Whether anyone could be looking at this. Deliberately weaker than the test
 * behind `setForeground`, which also wants the window focused: a chat left
 * visible behind another window still shows «нет соединения» to whoever glances
 * at it, and that glance is the whole subject of an outage row.
 */
function onScreen(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

/**
 * One live connection for the whole app. On the web it authenticates with the
 * session cookie the browser already sends on the upgrade request, so there is
 * no token to pass around; a native build, which is cross-origin and has no
 * cookie to send, puts the session in the query instead — see wsUrl, which owns
 * that difference and explains why a header is not an option here.
 */
class ChatSocket {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private retryMs = RECONNECT_MIN_MS;
  private reconnectTimer: number | null = null;
  private wantOpen = false;
  private foreground = false;
  /** Which conversation the last foreground claim named, if any. */
  private watching: string | null = null;
  /**
   * Whether the outage this socket is in has already been written down. One row
   * per outage, not one per retry: the reconnect backoff tops out at 15s, so a
   * night with no network would otherwise file thousands of identical notes
   * saying the same thing the first one said.
   */
  private outageReported = false;
  /** When the connection was last up, so the note can say how long it lasted. */
  private lastOpenAt: number | null = null;
  /**
   * When the live connection was lost — the *first* close of this outage, not
   * the last failed retry. What is being measured is how long the app has been
   * without a server, which is the only thing that separates a drop worth
   * reporting from the ones it recovers from unnoticed.
   */
  private outageStartedAt: number | null = null;
  /**
   * How much of the current outage the app has spent on screen, and when the
   * stretch it is in began — null while the app is hidden, or connected.
   *
   * This is the figure the outage is judged on rather than the wall clock; see
   * lib/outage.ts for why a phone in a pocket is not having an outage.
   */
  private visibleDownMs = 0;
  private visibleDownSince: number | null = null;
  private watchingVisibility = false;

  connect(): void {
    this.wantOpen = true;
    this.watchVisibility();
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    // A hidden page defers its socket until somebody actually looks at it —
    // watchVisibility connects then. The case this exists for: iOS relaunches
    // the killed app *in the background* to serve a gallery-sync window, the
    // WebView boots with it, and this connection was stamping the person
    // «в сети только что» from a phone lying untouched on a shelf. Presence
    // is a claim about a person, and a hidden page has no person behind it.
    // An app merely backgrounded keeps its already-open socket — this guards
    // only the first connection of a process nobody has seen.
    if (!onScreen()) return;

    const socket = new WebSocket(wsUrl());
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.retryMs = RECONNECT_MIN_MS;
      this.outageReported = false;
      this.outageStartedAt = null;
      this.visibleDownMs = 0;
      this.visibleDownSince = null;
      this.lastOpenAt = Date.now();
      // Re-assert visibility before anything else: the server has no memory of
      // the socket that just died, and until it is told, this user looks
      // backgrounded and would be sent an OS notification for a message that
      // is about to appear on screen.
      this.setForeground(this.foreground, this.watching);
      this.emitStatus(true);
    });

    socket.addEventListener('message', (event) => {
      // A socket this object has already replaced is on its way out and its
      // events are duplicates of the live one's — see the close handler.
      if (this.socket !== socket) return;
      let parsed: ServerEvent;
      try {
        parsed = JSON.parse(String(event.data)) as ServerEvent;
      } catch {
        return;
      }
      for (const listener of this.listeners) listener(parsed);
    });

    // Only the socket still held here may report its own death. A closing socket
    // outlives the call that closed it, so a disconnect immediately followed by
    // a connect — a logout and a login, or React's mount/unmount/mount in
    // development — used to have the first one's `close` arrive after the second
    // was already open, null out the live socket and schedule a reconnect on top
    // of it. Two connections then delivered every message twice, which nothing
    // noticed while every handler was idempotent and the unread badge is not.
    socket.addEventListener('close', (event) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.emitStatus(false);
      if (!this.wantOpen) return;
      // wantOpen is what separates the two ways a socket ends: a logout closed
      // it on purpose and has nothing to report, while everything else is the
      // live connection going away under an app that still wants it.
      this.reportOutage(event);
      this.scheduleReconnect();
    });

    // 'error' is always followed by 'close', which owns the reconnect.
    socket.addEventListener('error', () => socket.close());
  }

  disconnect(): void {
    this.wantOpen = false;
    // Whatever outage was in progress ends here with the app no longer wanting a
    // socket at all; leaving its clock running would let a signing-in device
    // inherit the previous session's downtime and report on its first stumble.
    this.outageStartedAt = null;
    this.outageReported = false;
    this.visibleDownMs = 0;
    this.visibleDownSince = null;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    // Said here rather than left to the close event, which the guard above now
    // silences for a socket this object has already let go of.
    if (socket) this.emitStatus(false);
  }

  /** Tells the server the viewer is looking at this conversation. */
  markRead(conversationId: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'read', conversationId }));
    }
  }

  /**
   * Relayed to the other person as «печатает…». Dropped when the socket is
   * down rather than queued: by the time it reconnected the claim would be
   * about something that was typed a reconnect ago.
   */
  setTyping(conversationId: string, typing: boolean): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'typing', conversationId, typing }));
    }
  }

  /**
   * Tells the server whether this window is actually on screen. An open socket
   * is not the same thing — a backgrounded tab keeps its socket for minutes —
   * and this is what decides whether a message is pushed to the OS or simply
   * delivered here. Remembered so a reconnect re-reports it: the server tracks
   * this per socket, and a new socket starts out assuming nothing.
   *
   * `conversationId` is what narrows it from "in the app" to "reading this".
   * Only the thread on screen has the message delivered into it; from the chat
   * list, a notification is the only way to hear about one, so `null` there is
   * not a detail — it is the difference between being told and not.
   *
   * The server expires this claim, so while visible it has to be repeated: a
   * phone that is suspended mid-frame never delivers its `false`, and only a
   * claim that stops being renewed can be distinguished from one that is still
   * true. Callers send it on every visibility change, on every change of open
   * conversation, and on a timer.
   */
  setForeground(active: boolean, conversationId: string | null = null): void {
    this.foreground = active;
    this.watching = conversationId;
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'focus', active, conversationId }));
    }
  }

  // ---- calls ----
  //
  // These carry a colon like the events coming back, unlike the three senders
  // above. A call is one conversation held in both directions, and spelling both
  // halves the same way is worth the inconsistency.
  //
  // None of them queue when the socket is down. There is nothing useful to do
  // with a hang-up that arrives after a reconnect, and the server has already
  // ended the call by then anyway — losing the socket is one of the things that
  // ends one.

  /** Places a call in a 1:1 chat. The reply is `call:accepted` or `call:ended`. */
  invite(conversationId: string, video: boolean): void {
    this.send({ type: 'call:invite', conversationId, video });
  }

  /**
   * Picks up. `video: false` is «Ответить без видео» — the call stays a video
   * call, this side just does not send a picture.
   */
  accept(callId: string, video: boolean): void {
    this.send({ type: 'call:accept', callId, video });
  }

  decline(callId: string): void {
    this.send({ type: 'call:decline', callId });
  }

  /** Ends a call at any stage: cancelling one's own ringing call, or hanging up. */
  hangup(callId: string): void {
    this.send({ type: 'call:hangup', callId });
  }

  /** One SDP or ICE payload for the other side. The server does not read it. */
  signal(callId: string, payload: unknown): void {
    this.send({ type: 'call:signal', callId, payload });
  }

  private send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  onEvent(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Fires on every connect and disconnect; `true` also means "resync now". */
  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private emitStatus(connected: boolean): void {
    for (const listener of this.statusListeners) listener(connected);
  }

  /**
   * Writes down that the live connection went away and stayed away, once per
   * outage.
   *
   * The close code is the whole value of this: 1006 is "the connection died and
   * nobody said why", which is what a phone losing signal looks like, while a
   * code the server sent means the server was reached and refused. Those are
   * different problems and they are indistinguishable from the chat list, which
   * shows the same «нет соединения» for both. It is also why only one of them
   * has to prove itself first — see lib/outage.ts, which owns that rule.
   *
   * Called from every close, including the ones that are a failed reconnect
   * attempt: those are what carry the outage clock forward until the rule
   * decides there is something to say.
   */
  private reportOutage(event: CloseEvent): void {
    if (this.outageReported) return;

    const now = Date.now();
    if (this.outageStartedAt === null) {
      this.outageStartedAt = now;
      this.visibleDownMs = 0;
      this.visibleDownSince = onScreen() ? now : null;
    }

    const note = outageNote({
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
      // Measured to the moment the connection was lost rather than to now, so a
      // long outage does not report itself as a long session.
      uptimeMs: this.lastOpenAt === null ? null : this.outageStartedAt - this.lastOpenAt,
      downForMs: now - this.outageStartedAt,
      visibleDownForMs: this.visibleDownFor(now),
    });
    if (!note) return;

    this.outageReported = true;
    reportClientError({ kind: 'socket', ...note });
  }

  private visibleDownFor(now: number): number {
    return this.visibleDownMs + (this.visibleDownSince === null ? 0 : now - this.visibleDownSince);
  }

  /**
   * Keeps the on-screen half of the outage clock, which only ever runs between
   * a close and the reconnect that ends it.
   *
   * Installed on the first connect rather than at import, so that loading this
   * module somewhere without a document costs nothing, and never removed —
   * there is one socket for the life of the page.
   */
  private watchVisibility(): void {
    if (this.watchingVisibility) return;
    this.watchingVisibility = true;

    document.addEventListener('visibilitychange', () => {
      // The other half of the hidden-page guard in connect(): the moment the
      // page is looked at, the deferred connection happens. Nothing to do when
      // a socket or a retry timer already exists — those paths own themselves.
      if (onScreen() && this.wantOpen && !this.socket && this.reconnectTimer === null) {
        this.connect();
      }
      if (this.outageStartedAt === null) return;
      if (onScreen()) {
        this.visibleDownSince ??= Date.now();
        return;
      }
      if (this.visibleDownSince !== null) {
        this.visibleDownMs += Date.now() - this.visibleDownSince;
        this.visibleDownSince = null;
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = this.retryMs;
    this.retryMs = Math.min(this.retryMs * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wantOpen) this.connect();
    }, delay);
  }
}

export const chatSocket = new ChatSocket();
